import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RevealItem } from 'shared/components/RevealItem/RevealItem';
import { useScrollReveal } from 'shared/hooks/useScrollReveal';
import { EventListItem } from '../components/EventListItem/EventListItem';
import { EventItem } from 'shared/data/events';

interface Props {
  events: EventItem[]
}

export function SacredCalendarSection({events}: Props) {
  const { t } = useTranslation('community');
  const heading = useScrollReveal({ animation: 'float-up' });

  const futureEvents: EventItem[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return events.filter((event) => {
      return event.date >= today;
    });
  }, [events])

  const nextEvent = futureEvents[0]

  const nextEventDate = nextEvent.date

  const monthStr = nextEventDate.toLocaleString('en', { month: 'long' })

  return (
    <section className="px-6 lg:px-24 py-12 md:py-24">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-16">
        <div ref={heading.ref} style={heading.style} className="md:w-1/3">
          <h2 className="font-serif text-5xl mb-6">
            {t('calendar.title')} <br />
            <span className="italic text-tertiary">{t('calendar.highlight')}</span>
          </h2>
          <p className="text-on-surface-variant mb-8">
            {t('calendar.description')}
          </p>

          <div className="p-6 bg-surface-container-high rounded-2xl border-l-4 border-tertiary">
            <p className="text-xs uppercase tracking-widest text-tertiary mb-2">
              {t('calendar.nextGathering')}
            </p>
            <p className="font-serif text-xl">{nextEvent.title}</p>
            <p className="text-sm text-on-surface-variant mt-1">
              {`${monthStr} ${nextEventDate.getDay()} \u2022 ${nextEventDate.getTime()}`}
            </p>
          </div>
        </div>

        <ul className="md:w-2/3 space-y-4 list-none p-0 m-0">
          {events.map((event, i) => i > 0 ? (
            <RevealItem key={event.id} index={i}>
              <EventListItem {...event} />
            </RevealItem>
          ) : null)}
        </ul>
      </div>
    </section>
  );
}
